import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { AlertTriangle, TrendingUp, Bell, ChevronRight, ExternalLink, Target, Trophy, Clock, PlusCircle } from 'lucide-react';
import { MiniLeaderboard, MiniRevenueTrend, MiniContribution, PeopleWarnings, WinsAndMomentum } from '@/components/AnalyticsWidgets';
import { CostBadge } from '@/components/CostWidgets';
import GroupRevenueTrend from '@/components/GroupRevenueTrend';
import ReceivablesTracker from '@/components/ReceivablesTracker';
import TermsPendingWidget from '@/components/TermsPendingWidget';
import WhatsNewStrip from '@/components/WhatsNewStrip';
import PaceIndicator, { annualFYDays } from '@/components/PaceIndicator';
import SyncNowButton from '@/components/SyncNowButton';
import InvoiceSLATracker from '@/components/InvoiceSLATracker';
import IPRenewalSummary from '@/components/IPRenewalSummary';
import OperatorMiraTimeSavedStrip from '@/components/OperatorMiraTimeSavedStrip';
import RecentMiraActions from '@/components/RecentMiraActions';
import BackupStatusStrip from '@/components/BackupStatusStrip';
import SystemHealthTile from '@/components/SystemHealthTile';
import MorningBriefingCard from '@/components/MorningBriefingCard';
import AnomalyBanner from '@/components/AnomalyBanner';
import BriefcaseTile from '@/components/BriefcaseTile';
// TDBFoodCostKPI moved to /division/tdb on 18-May-2026 (was a TDB-only drill-down)
import EditableTarget from '@/components/EditableTarget';
import { GroupGapScreamer } from '@/components/GapScreamer';
import MissingSalesDaysPanel from '@/components/MissingSalesDaysPanel';
import XiaomiTodayTile from '@/components/XiaomiTodayTile';
import RedBerylReadinessTile from '@/components/RedBerylReadinessTile';
import SoulEnrichmentTile from '@/components/SoulEnrichmentTile';
import SoulEnginePulse from '@/components/SoulEnginePulse';

const fmt = (v) => {
  if (!v) return '0';
  // 2 decimals, strip trailing zeros so ₹15.28Cr stays precise but ₹14Cr doesn't show "14.00Cr".
  if (v >= 10000000) return `${(v/10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (v >= 100000) return `${(v/100000).toFixed(2).replace(/\.?0+$/, '')}L`;
  return Number(v).toLocaleString('en-IN');
};
const healthColors = { green: '#2D8B5E', amber: '#BA7517', red: '#D4726A', grey: '#9CA3AF' };
const healthBg = { green: '#ECFDF5', amber: '#FEF3C7', red: '#FEF2F2', grey: '#F3F4F6' };

export default function CEODashboard() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  // Bundle 2.9.1 (5 May 2026 · Dipali doctrine): a dashboard that
  // shows ₹1.37Cr while the live ticker shows ₹1.42Cr is broken.
  // We auto-refresh every 60 seconds AND cascade on Sync Now click.
  // `refreshNonce` is keyed onto the analytics-widgets wrapper so all
  // child components (Revenue Trend / Division Contribution / Outlet
  // Leaderboard / Wins / People Warnings) remount and refetch as a
  // single atomic group — no stale numbers anywhere on the page.
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);

  const loadDashboard = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    try {
      const [pRes, plRes] = await Promise.all([
        api.get('/group/portfolio'),
        api.get('/pipeline/ceo-summary').catch(() => ({ data: null })),
      ]);
      setData(pRes.data);
      setPipelineSummary(plRes.data);
      setRefreshNonce(n => n + 1);
      setLastRefreshAt(new Date());
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        await logout();
        navigate('/login');
        return;
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [logout, navigate]);

  // First load (with spinner) + 60-second silent auto-refresh thereafter.
  useEffect(() => {
    loadDashboard();
    const iv = setInterval(() => loadDashboard({ silent: true }), 60_000);
    return () => clearInterval(iv);
  }, [loadDashboard]);

  if (loading) return <div className="py-16 text-center" style={{ color: '#6B7280' }}>Loading command centre...</div>;
  if (!data) return (
    <div className="py-16 text-center space-y-3">
      <p style={{ color: '#D4726A' }}>Dashboard could not load.</p>
      <button onClick={loadDashboard} className="px-4 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="retry-btn">
        Retry
      </button>
      <p className="text-xs" style={{ color: '#9CA3AF' }}>If this persists, <button onClick={async () => { await logout(); navigate('/login'); }} className="underline" style={{ color: '#2A7B88' }}>sign out and log back in</button>.</p>
    </div>
  );

  const { group, divisions, red_alerts, opportunities, action_queue } = data;

  return (
    <div className="space-y-5" data-testid="ceo-dashboard">
      {/* Quick-action ribbon */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <button onClick={() => navigate('/guide')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-white font-semibold shadow-sm hover:shadow-md transition-shadow" style={{ background: 'linear-gradient(135deg, #2A7B88 0%, #1B2A4A 100%)' }} data-testid="quick-start-guide-link">
          🧭 How do I…? Quick-start guide
        </button>
        <button onClick={() => navigate('/training/launch-summary')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border hover:bg-[#F7F5F2] font-medium" style={{ borderColor: '#E5E1DC', color: '#2A7B88' }} data-testid="launch-summary-link">
          📘 Training Library — Launch Summary
        </button>
        <button onClick={() => navigate('/docs')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border hover:bg-[#F7F5F2] font-medium" style={{ borderColor: '#E5E1DC', color: '#2A7B88' }} data-testid="system-docs-link">
          📚 System Docs
        </button>
        {/* Sync Now — force-pulls eZee, then cascades a full dashboard
            refresh so all widgets land in lock-step. The 60-s silent
            poll above takes care of background freshness. */}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px]" style={{ color: '#9CA3AF' }} data-testid="ceo-auto-refresh-label">
            Auto-refresh · 60 s
            {lastRefreshAt && (
              <> · last {lastRefreshAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</>
            )}
          </span>
          <SyncNowButton
            divisionId="me"
            variant="light"
            testid="ceo-sync-btn"
            onComplete={() => loadDashboard({ silent: true })}
          />
        </div>
      </div>

      {/* What's New — recently shipped features with tour-me links */}
      <WhatsNewStrip />

      {/* Operator Mira time-saved strip — adoption habit loop */}
      <OperatorMiraTimeSavedStrip />
      <RecentMiraActions limit={10} compact={true} />
      <div className="h-3" />

      {/* === GROUP GAP SCREAMER (Dipali doctrine — "make it impossible to miss") === */}
      <GroupGapScreamer
        target={group.target}
        realised={group.ytd}
        divisions={divisions}
      />

      {/* Missing daily-sales audit — TDB + ME outlets together so the CEO */}
      {/* can see at a glance who hasn't uploaded yet (1-Jun-2026). */}
      <MissingSalesDaysPanel divisionFilter="tdb,me" />

      {/* Xiaomi 2026 · Today's activity — live preview of the EOD digest
          so Dipali doesn't have to wait until midnight to see today's
          running totals. Hidden for users without xiaomi access. */}
      <XiaomiTodayTile />

      {/* RedBeryl go-live checklist + Soul Enrichment review queue */}
      <RedBerylReadinessTile className="mt-3" />
      <SoulEnrichmentTile className="mt-3" />

      {/* All self-fetching, eZee-fed widgets sit inside this wrapper —
          its `key` bumps on every refresh (60-s auto OR Sync Now click)
          so they all remount and re-fetch as a single atomic group.
          No widget can show stale numbers while another is fresh. */}
      <div className="space-y-5" key={`auto-refresh-${refreshNonce}`} data-testid="ceo-refresh-zone">
        <div className="h-3" />
        <MorningBriefingCard className="mb-3" />
        <AnomalyBanner className="mb-3" />
        <BriefcaseTile user={user} />
        <div className="h-3" />
        <SystemHealthTile className="mb-3" />
        <BackupStatusStrip className="mb-3" />

        {/* Group-level 5-year Revenue Journey — the group story at a glance */}
        <GroupRevenueTrend />

        {/* TDBFoodCostKPI moved to /division/tdb on 18-May-2026 — it's a
            TDB-specific drill-down, not a group-level KPI, so it belongs
            inside the TDB division view alongside Leader Pulse + Boston
            matrix rather than on the CEO dashboard. */}

        {/* Active Receivables — upcoming PDCs, awaiting POs, and overdue items */}
        <ReceivablesTracker />

        {/* Payment Terms Setup — contracts still needing terms configured */}
        <TermsPendingWidget />
      </div>

      {/* === Q1: ARE WE WINNING? === */}
      <div data-testid="quadrant-winning">
        {/* Group health bar */}
        <div className="bg-white rounded-lg border p-5" style={{ borderColor: '#E5E1DC' }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Georgia, serif', color: '#1B2A4A' }}>Les Concierges Group</h2>
              <p className="text-xs" style={{ color: '#6B7280' }}>FY 26-27 Portfolio Health</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold" style={{ color: healthColors[group.health] }}>{group.locked_pct}%</span>
                <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: healthBg[group.health], color: healthColors[group.health] }}>{group.health === 'green' ? 'On Track' : group.health === 'amber' ? 'Watch' : 'Needs Attention'}</span>
              </div>
              <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
                {fmt(group.locked)} locked of{' '}
                <EditableTarget
                  value={group.target}
                  entityType="group"
                  entityId="global"
                  label="Group Target"
                  onSaved={loadDashboard}
                  testId="group-target-edit"
                  formatValue={fmt}
                />{' '}
                target
              </p>
            </div>
          </div>
          {/* Progress bar */}
          <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: '#F3F4F6' }}>
            <div className="h-full rounded-full flex">
              {group.ytd > 0 && <div style={{ width: `${Math.min(group.ytd / group.target * 100, 100)}%`, backgroundColor: '#2D8B5E' }} title={`Realised: ${fmt(group.ytd)}`} />}
              {group.contracted > 0 && <div style={{ width: `${Math.min(group.contracted / group.target * 100, 100 - (group.ytd / group.target * 100))}%`, backgroundColor: '#3B5BA9' }} title={`Contracted: ${fmt(group.contracted)}`} />}
            </div>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[10px]" style={{ color: '#6B7280' }}>
            <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: '#2D8B5E' }} />Realised: {fmt(group.ytd)}</span>
            <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: '#3B5BA9' }} />Contracted: {fmt(group.contracted)}</span>
            <span title="Target − max(Realised, Contracted). What's still uncovered after counting cheques + signed contracts."><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: '#F3F4F6', border: '1px solid #E5E1DC' }} />Uncovered Gap: {fmt(group.target - group.locked)}</span>
            <span style={{ color: '#9CA3AF' }} title="Target − Realised. What's not yet cash-in.">· Unrealised vs target: {fmt(Math.max(0, group.target - group.ytd))}</span>
          </div>
        </div>

        {/* Division health cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-3">
          {divisions.map(d => (
            <div key={d.id} onClick={() => navigate(`/division/${d.id}`)} className="bg-white rounded-[10px] border p-4 cursor-pointer hover:shadow-md transition-shadow"
              style={{ borderColor: '#E5E1DC' }} data-testid={`div-card-${d.id}`}>
              <div className="flex items-center justify-between mb-1">
                {/* Status pill */}
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[10px] text-[11px] font-medium"
                  style={{
                    backgroundColor: d.health === 'green' ? '#EAF3DE' : d.health === 'amber' ? '#FAEEDA' : d.health === 'red' ? '#FCEBEB' : '#F3F4F6',
                    color: d.health === 'green' ? '#3B6D11' : d.health === 'amber' ? '#854F0B' : d.health === 'red' ? '#A32D2D' : '#6B7280',
                  }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: healthColors[d.health] }} />
                  {d.health === 'green' ? 'On Track' : d.health === 'amber' ? 'Watch' : d.health === 'red' ? 'At Risk' : 'No Target'}
                </span>
                {d.is_overdue && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[10px] text-[11px] font-medium"
                    style={{ backgroundColor: '#FCEBEB', color: '#A32D2D' }}>OVERDUE</span>
                )}
              </div>
              <h3 className="text-sm font-bold mt-2" style={{ color: '#1B2A4A' }}>{d.name}</h3>
              <p className="text-[10px]" style={{ color: '#6B7280' }}>{d.leader_name}</p>
              <div className="mt-2">
                {/* Locked = max(realised, contracted). Contracted already
                    includes already-received schedule rows (RedBeryl April
                    cheque is in both buckets), so ytd+contracted would
                    double-count. Division page uses the same max() formula. */}
                <p className="text-[22px] font-medium" style={{ color: '#1B2A4A' }}>{fmt(Math.max(d.ytd || 0, d.contracted || 0))}</p>
                <p className="text-[10px]" style={{ color: '#6B7280' }}>
                  of{' '}
                  <EditableTarget
                    value={d.target}
                    entityType="division"
                    entityId={d.id}
                    label={d.name}
                    onSaved={loadDashboard}
                    testId={`div-target-edit-${d.id}`}
                    formatValue={fmt}
                  />
                  {d.target > 0 ? ` (${d.locked_pct}%)` : ''}
                </p>
              </div>
              {d.target > 0 && <div className="h-1.5 rounded-full mt-2" style={{ backgroundColor: '#F3F4F6' }}>
                <div className="h-full rounded-full" style={{ width: `${Math.min(d.locked_pct, 100)}%`, backgroundColor: healthColors[d.health] }} />
              </div>}
              {/* Pace — REALISED revenue only vs expected daily pace (Dipali rule, Feb 18 2026).
                  Contracts are signed deals with no daily burn rate → excluded from pace.
                  For contract divisions we also surface the locked %, so '0% invoiced · 71% locked'
                  reads as a single honest status line. */}
              {d.target > 0 && (() => {
                const { daysElapsed, daysInPeriod } = annualFYDays();
                const realised = d.ytd || 0;
                const contracted = d.contracted || 0;
                const contractHeavy = contracted > realised * 3 && contracted > 5_000_000;
                const lockedPct = Math.round((Math.max(realised, contracted) / d.target) * 100);
                return (
                  <div className="mt-2" data-testid={`ceo-pace-${d.id}`}>
                    <PaceIndicator
                      actual={realised}
                      target={d.target}
                      daysElapsed={daysElapsed}
                      daysInPeriod={daysInPeriod}
                      size="sm"
                      invoicedLabel={contractHeavy}
                    />
                    {contractHeavy && (
                      <p className="text-[10px] mt-1 leading-tight" style={{ color: '#6B7280' }} data-testid={`contract-note-${d.id}`}>
                        0% invoiced · <span style={{ color: '#3B6D11', fontWeight: 600 }}>{lockedPct}% locked</span>
                        {lockedPct >= 50 ? ' — strong book, billing not yet started' : ' — keep signing'}
                      </p>
                    )}
                  </div>
                );
              })()}
              <p className="text-[10px] mt-2 leading-tight" style={{ color: '#6B7280' }} data-testid={`div-status-line-${d.id}`}>{d.status_line}</p>
              {d.overdue_flag_text && (
                <p className="text-[10px] mt-0.5 leading-tight" style={{ color: '#A32D2D' }} data-testid={`div-overdue-flag-${d.id}`}>
                  ⚠ {d.overdue_flag_text}
                </p>
              )}
              {d.wow_change !== 0 && (
                <p className="text-[11px] mt-0.5" style={{ color: d.wow_change > 0 ? '#3B6D11' : '#A32D2D' }} data-testid={`div-wow-${d.id}`}>
                  {d.wow_change > 0 ? '▲' : '▼'} {d.wow_change > 0 ? '+' : ''}{d.wow_change}% WoW
                  {d.week_window_label && (
                    <span className="ml-1 text-[9px]" style={{ color: '#9CA3AF' }}>· {d.week_window_label}</span>
                  )}
                </p>
              )}
              {d.id === 'me' && <div className="mt-1"><CostBadge /></div>}
            </div>
          ))}
        </div>
      </div>

      {/* === ANALYTICS SUMMARY: Trend + Contribution + Leaderboard ===
          The same `auto-refresh-${refreshNonce}` key wrapper used above
          is applied here too — so on every 60-s tick or Sync Now click,
          the Outlet Leaderboard, Division Contribution donut, and
          Revenue Trend chart all remount and re-pull from
          `daily_sales`. Net effect: when a Celeste sale lands at
          20:15 IST, the next intra-day cron mirrors it to
          `daily_sales` within 30 minutes, and inside 60 seconds the
          dashboard reflects it without any human action. */}
      <div key={`auto-refresh-analytics-${refreshNonce}`} data-testid="ceo-analytics-zone" className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="ceo-analytics-summary">
          <MiniRevenueTrend />
          <MiniContribution />
          <MiniLeaderboard />
        </div>

        {/* People Warnings — across all divisions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <WinsAndMomentum />
          <PeopleWarnings />
        </div>
      </div>

      {/* First-Invoice SLA across contract divisions — surfaces "sign-and-forget" deals */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <InvoiceSLATracker thresholdDays={30} />
        </div>
        <IPRenewalSummary />
      </div>

      {/* Customer Intelligence + Soul Engine pulse */}
      <SoulEnginePulse />

      {/* Bottom grid: 3 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* === Q2: BIGGEST PROBLEM === */}
        <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="quadrant-problems">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} style={{ color: '#D4726A' }} />
            <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Problems</h3>
            {red_alerts.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }}>{red_alerts.length}</span>}
          </div>
          {red_alerts.length === 0 ? (
            <p className="text-xs py-4 text-center" style={{ color: '#9CA3AF' }}>All clear. No red flags.</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {red_alerts.map((a, i) => (
                <div key={i} onClick={() => a.division_id && navigate(`/division/${a.division_id}`)} className="flex items-start gap-2 p-2 rounded cursor-pointer hover:bg-gray-50"
                  style={{ backgroundColor: a.severity === 'critical' ? '#FEF2F2' : '#FFFBEB' }}>
                  <span className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: a.severity === 'critical' ? '#D4726A' : '#BA7517' }} />
                  <p className="text-xs leading-snug" style={{ color: '#1B2A4A' }}>{a.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* === Q3: OPPORTUNITIES + PIPELINE === */}
        <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="quadrant-opportunities">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={16} style={{ color: '#2D8B5E' }} />
            <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Opportunities</h3>
            {(opportunities.length + (pipelineSummary?.won_this_month?.count || 0)) > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#ECFDF5', color: '#2D8B5E' }}>
                {opportunities.length + (pipelineSummary?.won_this_month?.count || 0)}
              </span>
            )}
            <a href="/sales-group" className="ml-auto text-[10px] font-semibold px-2 py-1 rounded-md"
               style={{ backgroundColor: '#2A7B88', color: 'white' }} data-testid="ceo-group-sales-link">
              Open Group Sales System →
            </a>
          </div>

          {/* Pipeline summary cards */}
          {pipelineSummary && (
            <div className="grid grid-cols-4 gap-2 mb-3">
              <div className="rounded-md p-2 text-center" style={{ backgroundColor: '#F0FDF4' }}>
                <p className="text-[10px]" style={{ color: '#6B7280' }}>Active Pipeline</p>
                <p className="text-sm font-bold" style={{ color: '#2A7B88' }}>
                  {pipelineSummary.active_deals || 0} <span className="text-[10px] font-normal" style={{ color: '#6B7280' }}>deals</span>
                </p>
                <p className="text-[10px]" style={{ color: '#2A7B88' }}>{fmt(pipelineSummary.total_weighted_pipeline)} <span style={{ color: '#9CA3AF' }}>wtd</span></p>
              </div>
              <div className="rounded-md p-2 text-center" style={{ backgroundColor: '#ECFDF5' }}>
                <p className="text-[10px]" style={{ color: '#6B7280' }}>Won This Month</p>
                <p className="text-sm font-bold" style={{ color: '#2D8B5E' }}>{pipelineSummary.won_this_month?.count || 0}</p>
                {pipelineSummary.won_this_month?.value > 0 && <p className="text-[10px]" style={{ color: '#2D8B5E' }}>{fmt(pipelineSummary.won_this_month.value)}</p>}
              </div>
              <div className="rounded-md p-2 text-center" style={{ backgroundColor: '#F7F5F2' }}>
                <p className="text-[10px]" style={{ color: '#6B7280' }}>New This Week</p>
                <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{pipelineSummary.new_this_week || 0}</p>
              </div>
              <div className="rounded-md p-2 text-center" style={{ backgroundColor: pipelineSummary.stuck_deals?.length > 0 ? '#FEF2F2' : '#F7F5F2' }}>
                <p className="text-[10px]" style={{ color: '#6B7280' }}>Stuck Deals</p>
                <p className="text-sm font-bold" style={{ color: pipelineSummary.stuck_deals?.length > 0 ? '#D4726A' : '#1B2A4A' }}>{pipelineSummary.stuck_deals?.length || 0}</p>
              </div>
            </div>
          )}

          {/* Won deals this month — celebrations */}
          {pipelineSummary?.won_this_month?.count > 0 && (
            <div className="mb-2">
              {/* Show by_division won deals */}
              {Object.entries(pipelineSummary.by_division || {}).filter(([, s]) => s.won > 0).map(([div, s]) => (
                <div key={div} onClick={() => navigate(`/division/${div}`)} className="flex items-start gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 mb-1" style={{ backgroundColor: '#F0FDF4' }}>
                  <Trophy size={12} style={{ color: '#2D8B5E' }} className="mt-0.5 flex-shrink-0" />
                  <p className="text-xs leading-snug" style={{ color: '#1B2A4A' }}>{div}: {s.won} deals won ({fmt(s.won_value)})</p>
                </div>
              ))}
            </div>
          )}

          {/* Stuck deals — need attention */}
          {pipelineSummary?.stuck_deals?.length > 0 && (
            <div className="mb-2">
              {pipelineSummary.stuck_deals.slice(0, 3).map((d, i) => (
                <div key={i} onClick={() => d.division_id && navigate(`/division/${d.division_id}`)} className="flex items-start gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 mb-1" style={{ backgroundColor: '#FEF2F2' }}>
                  <Clock size={12} style={{ color: '#D4726A' }} className="mt-0.5 flex-shrink-0" />
                  <p className="text-xs leading-snug" style={{ color: '#1B2A4A' }}>{d.deal}: stuck in {d.stage} for {d.days}d ({fmt(d.value)})</p>
                </div>
              ))}
            </div>
          )}

          {/* Existing opportunities from portfolio */}
          {opportunities.length > 0 && (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {opportunities.map((o, i) => (
                <div key={i} onClick={() => o.division_id && navigate(`/division/${o.division_id}`)} className="flex items-start gap-2 p-2 rounded cursor-pointer hover:bg-gray-50" style={{ backgroundColor: '#F0FDF4' }}>
                  <span className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: '#2D8B5E' }} />
                  <p className="text-xs leading-snug" style={{ color: '#1B2A4A' }}>{o.message}</p>
                </div>
              ))}
            </div>
          )}

          {opportunities.length === 0 && !pipelineSummary?.won_this_month?.count && (
            <p className="text-xs py-2 text-center" style={{ color: '#9CA3AF' }}>No positive streaks yet this week.</p>
          )}
        </div>

        {/* === Q4: WHO NEEDS ATTENTION === */}
        <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="quadrant-attention">
          <div className="flex items-center gap-2 mb-3">
            <Bell size={16} style={{ color: '#BA7517' }} />
            <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Needs Attention</h3>
            {action_queue.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#FEF3C7', color: '#BA7517' }}>{action_queue.length}</span>}
          </div>
          {action_queue.length === 0 ? (
            <p className="text-xs py-4 text-center" style={{ color: '#9CA3AF' }}>Nothing pending. All leaders on track.</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {action_queue.map((a, i) => (
                <div key={i} onClick={() => a.division_id && navigate(`/division/${a.division_id}`)} className="flex items-start gap-2 p-2 rounded cursor-pointer hover:bg-gray-50">
                  <span className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: a.type === 'overdue' ? '#D4726A' : '#BA7517' }} />
                  <div className="flex-1">
                    <p className="text-xs leading-snug" style={{ color: '#1B2A4A' }}>{a.message}</p>
                    <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{a.type === 'overdue' ? 'Weekly update' : a.type === 'flagged' ? 'Needs your help' : 'Action needed'}</p>
                  </div>
                  <ChevronRight size={14} style={{ color: '#9CA3AF' }} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
